import React from "react";
import { mutate } from "swr";
import { toast } from "react-toastify";
import NavTop from "../../component/NavTop";
import ButtonLoading from "../../component/ButtonLoading";
import {
  IoIosCopy,
  IoIosCheckmarkCircle,
  IoIosWarning,
  IoIosTrash,
  IoIosRefresh,
  IoIosEye,
  IoIosLink,
  IoLogoWhatsapp,
} from "react-icons/io";
import {
  useCompanyShare,
  useWallet,
  enableShare,
  updateShareWallets,
  revokeShare,
} from "../../api";
import "./share.css";

function shareUrl(token) {
  const origin =
    typeof window !== "undefined" ? window.location.origin : "";
  return `${origin}/share/${token}`;
}

export default function ShareSettings() {
  const { data: state, error } = useCompanyShare();
  const { wallets } = useWallet();
  const [orgName, setOrgName] = React.useState("");
  const [excluded, setExcluded] = React.useState([]);
  const [busy, setBusy] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  // Hydrate local state once when first load returns
  const hydratedRef = React.useRef(false);
  React.useEffect(() => {
    if (!state || hydratedRef.current) return;
    hydratedRef.current = true;
    if (state.publicOrgName) setOrgName(state.publicOrgName);
    if (Array.isArray(state.publicExcludedWalletIds))
      setExcluded(state.publicExcludedWalletIds);
  }, [state]);

  const token = state && state.publicShareToken;
  const isActive = !!token;
  const url = isActive ? shareUrl(token) : "";

  const toggleWallet = async (walletId) => {
    const next = excluded.includes(walletId)
      ? excluded.filter((x) => x !== walletId)
      : [...excluded, walletId];
    setExcluded(next);
    if (isActive) {
      try {
        await updateShareWallets(next);
        await mutate("/company/share");
      } catch (_) {
        toast.error("Gagal memperbarui wallet yang dibagikan.");
        // Roll back optimistic update
        setExcluded(excluded);
      }
    }
  };

  const handleEnable = async () => {
    const name = orgName.trim();
    if (!name) {
      toast.error("Nama organisasi wajib diisi.");
      return;
    }
    setBusy(true);
    try {
      await enableShare(name, excluded);
      await mutate("/company/share");
      toast.success("Link berbagi diaktifkan.");
    } catch (err) {
      const msg =
        err.response && err.response.data && err.response.data.error
          ? err.response.data.error.message
          : "Gagal mengaktifkan link berbagi.";
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const handleRegenerate = async () => {
    if (
      !window.confirm(
        "Buat link baru? Link lama akan langsung tidak berlaku."
      )
    )
      return;
    setBusy(true);
    try {
      await enableShare(orgName.trim(), excluded);
      await mutate("/company/share");
      toast.success("Link baru dibuat. Link lama sudah tidak berlaku.");
    } catch (_) {
      toast.error("Gagal membuat link baru.");
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async () => {
    if (
      !window.confirm(
        "Cabut link berbagi? Setelah dicabut, viewer tidak bisa lagi melihat data lewat link ini."
      )
    )
      return;
    setBusy(true);
    try {
      await revokeShare();
      await mutate("/company/share");
      toast.success("Link berbagi dicabut.");
    } catch (_) {
      toast.error("Gagal mencabut link.");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (_) {
      toast.error("Gagal menyalin link.");
    }
  };

  const shareWaText = encodeURIComponent(
    `Catatan keuangan ${orgName || "organisasi kami"} bisa dilihat di sini:\n${url}\n\nLink hanya dapat dilihat dari pesan ini — tidak akan muncul di pencarian Google.`
  );

  if (error && !state) {
    return (
      <>
        <NavTop title="Berbagi catatan" />
        <div className="main-padding">
          <div className="share-alert">Gagal memuat data berbagi.</div>
        </div>
      </>
    );
  }

  if (!state) {
    return (
      <>
        <NavTop title="Berbagi catatan" />
        <div className="main-padding share-page">
          <div className="share-skeleton">Memuat…</div>
        </div>
      </>
    );
  }

  return (
    <>
      <NavTop title="Berbagi catatan" />
      <div className="main-padding share-page">
        <section className="share-intro">
          <h2 className="share-intro__title">
            Bagikan catatan ke organisasi
          </h2>
          <p className="share-intro__body">
            Buat satu link yang dapat dilihat anggota organisasi (mis. donatur,
            jamaah, pengurus RT). Mereka tidak perlu login — cukup buka link
            di browser. Halaman hanya bisa <strong>dilihat</strong>, tidak ada
            tombol edit atau hapus.
          </p>
          <p className="share-intro__body share-intro__body--muted">
            Link tidak akan muncul di pencarian Google. Hanya orang yang
            punya link bisa membukanya.
          </p>
        </section>

        <section className="share-card">
          <label className="share-label" htmlFor="org-name">
            Nama organisasi <span className="share-label__req">*</span>
          </label>
          <input
            id="org-name"
            type="text"
            className="share-input"
            placeholder="Contoh: RT 03 RW 05 Cipete"
            value={orgName}
            disabled={busy}
            maxLength={100}
            onChange={(e) => setOrgName(e.target.value)}
          />
          <div className="share-hint">
            Ditampilkan di halaman viewer sebagai pemilik catatan.
          </div>

          <div className="share-wallets">
            <div className="share-wallets__head">
              <span>Pilih wallet yang dibagikan</span>
              <span className="share-wallets__count">
                {wallets.length - excluded.length}/{wallets.length}
              </span>
            </div>
            <ul className="share-wallets__list">
              {wallets.length === 0 && (
                <li className="share-wallets__empty">Belum ada wallet.</li>
              )}
              {wallets.map((w) => {
                const checked = !excluded.includes(w.id);
                return (
                  <li key={w.id} className="share-wallets__item">
                    <label className="share-wallets__row">
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={busy}
                        onChange={() => toggleWallet(w.id)}
                      />
                      <span className="share-wallets__name">{w.name}</span>
                      {w.description && (
                        <span className="share-wallets__desc">
                          {w.description}
                        </span>
                      )}
                    </label>
                  </li>
                );
              })}
            </ul>
            <div className="share-hint">
              Wallet yang tidak dicentang tidak akan muncul di halaman viewer.
            </div>
          </div>

          {!isActive ? (
            busy ? (
              <ButtonLoading />
            ) : (
              <button
                type="button"
                className="share-btn share-btn--primary"
                onClick={handleEnable}
                disabled={!orgName.trim()}
              >
                <IoIosLink /> Aktifkan link berbagi
              </button>
            )
          ) : (
            <>
              <div className="share-link-row">
                <span className="share-link__icon">
                  <IoIosEye />
                </span>
                <div className="share-link__url" title={url}>
                  {url}
                </div>
                <button
                  type="button"
                  className="share-link__btn"
                  onClick={copy}
                  title="Salin link"
                  aria-label="Salin link"
                >
                  {copied ? (
                    <>
                      <IoIosCheckmarkCircle /> Tersalin
                    </>
                  ) : (
                    <>
                      <IoIosCopy /> Salin
                    </>
                  )}
                </button>
              </div>

              <a
                className="share-btn share-btn--wa"
                target="_blank"
                rel="noopener noreferrer"
                href={`https://wa.me/?text=${shareWaText}`}
              >
                <IoLogoWhatsapp /> Bagikan via WhatsApp
              </a>

              <div className="share-actions">
                <button
                  type="button"
                  className="share-btn share-btn--ghost"
                  onClick={handleRegenerate}
                  disabled={busy}
                >
                  <IoIosRefresh /> Buat link baru
                </button>
                <button
                  type="button"
                  className="share-btn share-btn--danger"
                  onClick={handleRevoke}
                  disabled={busy}
                >
                  <IoIosTrash /> Cabut link
                </button>
              </div>

              <div className="share-tip">
                <IoIosWarning />
                <span>
                  Jika link bocor ke orang yang tidak diinginkan, klik{" "}
                  <strong>Buat link baru</strong> — link lama langsung tidak
                  berlaku.
                </span>
              </div>
            </>
          )}
        </section>
      </div>
    </>
  );
}
